import { DirectionalHint, ITooltipHostProps, TooltipDelay, TooltipHost } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import React from 'react';

export interface TooltipItem {
  header?: string;
  text: string;
}

export interface ButtonTooltipProps extends Omit<ITooltipHostProps, 'content'> {
  className?: string;
  content?: TooltipItem | TooltipItem[] | string;
  children: React.ReactElement;
}

export const ButtonTooltip: React.FC<ButtonTooltipProps> = ({
  className,
  content,
  children,
  delay = TooltipDelay.long,
  ...props
}) => {
  const tooltipId = useId('tooltip');

  const hasContent = (typeof content === 'string' && content.trim() !== '')
    || (Array.isArray(content) && content.length > 0)

  if (!hasContent) {
    return children;
  }

  const tooltipProps = {
    onRenderContent: () => <div>{formatTooltip(content)}</div>,
    delay,
  };

  return (
    <TooltipHost
      className={className}
      id={tooltipId}
      tooltipProps={tooltipProps}
      directionalHint={DirectionalHint.topCenter}
      styles={{ root: { display: 'inline-block', width: '100%' } }}
      {...props}
    >
      <div aria-describedby={tooltipId}>
        {children}
      </div>
    </TooltipHost>
  );
};

function formatTooltip(content: TooltipItem | TooltipItem[] | string): React.ReactNode {
  if (typeof (content) === 'string') { return content; }
  const contentArray = Array.isArray(content) ? content : [content];
  if (contentArray.length === 1) { return contentArray[0].text; }

  return (
    contentArray.map(({ text, header }, i) => (
      <div key={i}>
        {header && <span style={{ fontWeight: 'bold' }}>{`${header}: `}</span>}
        <span>{text}</span><br />
      </div>
    ))
  );
}

export default ButtonTooltip;
